import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import VoidExpenseModal from "@/components/accounts/VoidExpenseModal";

export default function ProjectExpensesTab() {
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ project_id: "", category: "", from: "", to: "" });
  const [voidTarget, setVoidTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const params = { type: "project_direct" };
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.category) params.category = filters.category;
    const { data } = await api.get("/expenses", { params });
    let items = data.items || [];
    // Client-side date range filter (backend has no date-range filter for general list yet).
    if (filters.from) items = items.filter((e) => (e.date || "") >= filters.from);
    if (filters.to) items = items.filter((e) => (e.date || "") <= filters.to);
    setExpenses(items);
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters]);

  useEffect(() => {
    (async () => {
      const [p, c] = await Promise.all([
        api.get("/accounts/projects"),
        api.get("/catalogs"),
      ]);
      setProjects(p.data.items || []);
      setCategories((c.data.expense_categories || []).filter((c) => c.active));
    })();
  }, []);

  const projectMap = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

  const total = useMemo(
    () => expenses.filter((e) => !e.voided).reduce((sum, e) => sum + (e.amount || 0), 0),
    [expenses],
  );

  return (
    <div data-testid="project-expenses-tab">
      <div className="flex items-end gap-2 mb-4 flex-wrap">
        <Field label="Project">
          <select
            value={filters.project_id}
            onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
            className="px-3 py-1.5 text-sm border border-brand-border"
            style={{ borderRadius: 0 }}
            data-testid="project-expenses-filter-project"
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.client_name}{p.partner_name ? ` × ${p.partner_name}` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Category">
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="px-3 py-1.5 text-sm border border-brand-border"
            style={{ borderRadius: 0 }}
            data-testid="project-expenses-filter-category"
          >
            <option value="">All categories</option>
            {categories.filter((c) => c.default_type !== "studio_overhead").map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </Field>
        <Field label="From">
          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            className="px-3 py-1.5 text-sm border border-brand-border"
            style={{ borderRadius: 0 }}
            data-testid="project-expenses-filter-from"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            className="px-3 py-1.5 text-sm border border-brand-border"
            style={{ borderRadius: 0 }}
            data-testid="project-expenses-filter-to"
          />
        </Field>
        <div className="ml-auto text-sm">
          <div className="kicker">TOTAL</div>
          <div className="font-medium text-base" style={{ color: "var(--brand-danger)" }} data-testid="project-expenses-total">
            − {fmtINR(total)}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="kicker text-brand-muted">Loading</div>
      ) : expenses.length === 0 ? (
        <div className="bg-white border border-brand-border p-12 text-center" data-testid="project-expenses-empty">
          <p className="text-sm italic text-brand-muted">
            No project expenses match your filters. Add expenses from each project's payment slide-over.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-brand-border overflow-x-auto" data-testid="project-expenses-table">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.18em] uppercase text-brand-muted">
              <tr className="border-b border-brand-border">
                <th className="text-left p-3">Project</th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Description</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((e) => {
                const proj = projectMap[e.project_id];
                const name = proj
                  ? proj.client_name + (proj.partner_name ? ` × ${proj.partner_name}` : "")
                  : e.project_id;
                return (
                  <tr key={e.id} className="border-b border-brand-border" data-testid={`project-expense-row-${e.id}`}>
                    <td className="p-3">{name}</td>
                    <td className="p-3 text-brand-muted">{fmtDate(e.date)}</td>
                    <td className="p-3">{e.category}</td>
                    <td className="p-3 text-xs text-brand-muted">{e.description || "—"}</td>
                    <td className="p-3 text-right" style={{ color: "var(--brand-danger)" }}>
                      − {fmtINR(e.amount)}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => setVoidTarget(e)}
                        className="text-[10px] underline text-brand-muted"
                        data-testid={`project-expense-tab-void-${e.id}`}
                      >
                        Void
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {voidTarget && (
        <VoidExpenseModal
          expense={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={() => { setVoidTarget(null); refresh(); }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="kicker block mb-1">{label}</span>
      {children}
    </label>
  );
}
